import type { IAuthClient } from '@micro-frontend-react/employee-experience/lib/IAuthClient';
import type { SagaIterator } from 'redux-saga';
import { all, call, getContext, put, takeEvery } from 'redux-saga/effects';

import type { IPBTCase, IPBTCaseAudit, IUpdatePbtCaseRequest } from '../Interfaces/IPBTCase';
import { PBTApiEndpoints, PBTMessages } from './PBTConstants';
import { PBTCasesActionType } from '../Redux/PBT/Cases/action-types';
import { receiveCases, sendCasesError, updateCaseSuccess, updateCaseError } from '../Redux/PBT/Cases/actions';
import type { IUpdateCaseRequestAction } from '../Redux/PBT/Cases/action-types';

function auth(authClient: IAuthClient) {
  return Promise.resolve(authClient.acquireToken(__SCOPES__));
}

/** Uses native fetch instead of framework httpClient to avoid silent timeout issues */
async function fetchCasesApi(token: string, timeoutMs = 15000): Promise<Record<string, unknown>[]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(PBTApiEndpoints.Cases, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`API returned ${response.status}`);

    const json = await response.json();
    const envelope = json?.data ?? json?.Data;
    return envelope?.cases ?? envelope?.Cases ?? [];
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new Error(`API request timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Updates a PBT case with editable termination details */
async function updateCaseApi(
  token: string,
  caseId: number,
  request: IUpdatePbtCaseRequest,
  timeoutMs = 15000
): Promise<IPBTCase> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Serialize before the try so a serialization TypeError is not misreported as a
  // network failure; inside the try, a TypeError can then only be fetch's "Failed to fetch".
  const requestBody = JSON.stringify(request);

  try {
    const response = await fetch(PBTApiEndpoints.UpdateCase(caseId), {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: requestBody,
      signal: controller.signal,
    });

    if (!response.ok) {
      if (response.status === 403) {
        const json = await response.json();
        throw new Error(json?.error?.message ?? 'Case is locked - term email already sent');
      }
      if (response.status === 404) {
        throw new Error('Case not found');
      }
      throw new Error(`API returned ${response.status}`);
    }

    const json = await response.json();
    const envelope = json?.data ?? json?.Data;
    return envelope;
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new Error(`API request timed out after ${timeoutMs}ms`);
    }
    // Network failure (offline, blocked request, backend unreachable) surfaces as
    // a raw TypeError ("Failed to fetch"). Replace it with a friendly message.
    if (err instanceof TypeError) {
      throw new Error(PBTMessages.UpdateError);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Fetches the audit trail for a PBT case */
export async function getCaseAuditApi(
  token: string,
  caseId: number,
  timeoutMs = 15000
): Promise<IPBTCaseAudit[]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(PBTApiEndpoints.CaseAudit(caseId), {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`API returned ${response.status}`);

    const json = await response.json();
    const envelope = json?.data ?? json?.Data;
    return Array.isArray(envelope) ? envelope : [];
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new Error(`API request timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function* requestCasesSagas(): SagaIterator {
  yield all([
    takeEvery(PBTCasesActionType.REQUEST_CASES, fetchCases),
    takeEvery(PBTCasesActionType.UPDATE_CASE_REQUEST, updateCase),
  ]);
}

function* fetchCases(): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    const cases: Record<string, unknown>[] = yield call(fetchCasesApi, token);
    yield put(receiveCases(cases as unknown as IPBTCase[]));
  } catch (ex) {
    yield put(receiveCases([]));
    yield put(sendCasesError(PBTMessages.CasesError));
  }
}

function* updateCase(action: IUpdateCaseRequestAction): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    const updatedCase: IPBTCase = yield call(updateCaseApi, token, action.caseId, action.request);
    yield put(updateCaseSuccess(updatedCase));
  } catch (ex) {
    yield put(updateCaseError((ex as Error).message));
  }
}
